"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { EMAIL_RE } from "./use-email-recipients";
import { emailQueryOptions } from "@/api/provider/comms/email/email-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";

/**
 * A single autocomplete row. `contact`/`patient` come from the address book and
 * patient directory respectively; `raw` is the literal address the user typed,
 * always offered as a fallback (Gmail's "use what you typed").
 */
export interface RecipientSuggestion {
  kind: "contact" | "patient" | "staff" | "raw";
  email: string;
  /** Display name, when known. Null for raw addresses. */
  name: string | null;
}
export interface UseRecipientSuggestionsResult {
  suggestions: RecipientSuggestion[];
}
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Gmail-style recipient suggestions. Searches the tenant's email address book
 * (`EmailContact`) and the patient directory in parallel — two single-source
 * endpoints merged here, matching the platform's federate-on-the-client pattern
 * — deduped by email (address-book contacts win, they carry display names), and
 * always appends the raw typed address when it's a valid email.
 *
 * @param input   the live text in the recipient input
 * @param exclude already-committed recipient emails to hide from suggestions
 */
export function useRecipientSuggestions(input, exclude) {
  const $ = _c(23);
  let t0;
  if ($[0] !== input) {
    t0 = input.trim();
    $[0] = input;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const term = t0;
  const debounced = useDebounce(term, SEARCH_DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_SEARCH_LENGTH;
  let t1;
  if ($[2] !== debounced) {
    t1 = emailQueryOptions.contactSearch(debounced);
    $[2] = debounced;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== enabled || $[5] !== t1) {
    t2 = {
      ...t1,
      enabled,
      placeholderData: keepPreviousData
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const contactsQuery = useQuery(t2);
  let t3;
  if ($[7] !== debounced) {
    t3 = patientQueryOptions.search(debounced);
    $[7] = debounced;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== enabled || $[10] !== t3) {
    t4 = {
      ...t3,
      enabled,
      placeholderData: keepPreviousData
    };
    $[9] = enabled;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const patientsQuery = useQuery(t4);
  const contacts = contactsQuery.data;
  const patients = patientsQuery.data;
  const stale = contactsQuery.isPlaceholderData || patientsQuery.isPlaceholderData;
  let t5;
  bb0: {
    if (term.length < MIN_SEARCH_LENGTH) {
      let t6;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[13] !== exclude) {
      t6 = new Set(exclude.map(_temp));
      $[13] = exclude;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const excluded = t6;
    let out;
    if ($[15] !== contacts || $[16] !== excluded || $[17] !== patients || $[18] !== stale || $[19] !== term) {
      const seen = new Set();
      out = [];
      const push = suggestion => {
        const key = suggestion.email.toLowerCase();
        if (excluded.has(key) || seen.has(key)) {
          return;
        }
        seen.add(key);
        out.push(suggestion);
      };
      if (!stale) {
        for (const contact of contacts ?? []) {
          if (contact.email) {
            push({
              kind: "contact",
              email: contact.email,
              name: contact.displayName
            });
          }
        }
        for (const patient of patients ?? []) {
          if (patient.email) {
            const name = [patient.firstName, patient.lastName].filter(Boolean).join(" ");
            push({
              kind: "patient",
              email: patient.email,
              name: name || null
            });
          }
        }
      }
      const typed = term.toLowerCase();
      if (EMAIL_RE.test(term) && !seen.has(typed) && !excluded.has(typed)) {
        out.unshift({
          kind: "raw",
          email: term,
          name: null
        });
      }
      $[15] = contacts;
      $[16] = excluded;
      $[17] = patients;
      $[18] = stale;
      $[19] = term;
      $[20] = out;
    } else {
      out = $[20];
    }
    t5 = out;
  }
  const suggestions = t5;
  let t6;
  if ($[21] !== suggestions) {
    t6 = {
      suggestions
    };
    $[21] = suggestions;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}
function _temp(e) {
  return e.toLowerCase();
}